import React, {useCallback, useEffect, useMemo} from 'react';

import {recordRumAction} from '../../util/datadog/datadogRum';
import {useDeepEqualValue} from '../../util/hooks';
import {getDefaultLegendTemplate} from '../../util/legend';
import {useIsNgwEnabled} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {ErrorBoundary} from '../ErrorBoundary';
import {useRegexMetricsSync} from '../MetricSearch/useRegexMetricsSync';
import {useSingleRunModeContext} from '../MultiRunWorkspace/SingleRunMode';
import {useStablePanelId} from '../Panel/PanelIDContext';
import {useMaybeWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {useLinePlotSettingsWithDefaults} from '../WorkspaceDrawer/Settings/runLinePlots/utils/getLinePlotSettings';
import {logError} from './../../services/errors/errorReporting';
import {type RunSetQuery} from './../../util/queryTypes';
import {PanelRunsLinePlot} from './Component';
import {PanelOutliersRunsLinePlot} from './componentOutliers/ComponentOutliers';
import {runsLinePlotConfigDefaults} from './defaults';
import {PanelConfigContextProvider} from './PanelConfigContext';
import {PanelDataContextProvider} from './PanelDataContext';
import {PanelInteractionProvider} from './PanelInteractionContext';
import {PanelTimeProvider} from './PanelTimeContext';
import {PanelZoomContextProvider} from './PanelZoomContext';
import {
  RunsLinePlotContextProvider,
  useRunsLinePlotContext,
} from './RunsLinePlotContext/RunsLinePlotContext';
import {
  PanelGroupingContextProvider,
  usePanelGroupingSettings,
} from './RunsLinePlotContext/usePanelGroupingSettings';
import {useSharedPanelZoom} from './SharedPanelZoomContext';
import {PanelSystemsMetricsProvider} from './SystemMetrics/PanelSystemMetricsContext';
import {type RunsLinePlotPanelProps} from './types';
import {useRegexMetrics} from './useRegexMetrics';
import {getRunSets} from './utils';
import {PanelZoomContext2Provider} from './zoom/PanelZoomContext2';

const noOpArr: string[] = [];
const renderNothing = () => <></>;

const Wrapper = (props: RunsLinePlotPanelProps & {runSets: RunSetQuery[]}) => {
  const {isSingleRun} = useSingleRunModeContext();

  const {isFullFidelity, setRenderError} = useRunsLinePlotContext();

  const sharedPanelZoom = useSharedPanelZoom();

  const {coordinateZooming, fullFidelityMode, smoothingWeight, xAxis} =
    useLinePlotSettingsWithDefaults(props.config);

  const panelID = useStablePanelId();
  const plotType = props.config.plotType ?? runsLinePlotConfigDefaults.plotType;

  useEffect(() => {
    recordRumAction('line_plot_panel_rendered', {
      panelID,
      plotType,
    });
  }, [panelID, plotType]);

  const {isRunsetGrouped} = usePanelGroupingSettings();

  const getDefaultLegendTemplateByRunsetId = useCallback(
    (runsetId: string | undefined) =>
      getDefaultLegendTemplate({
        aggregateMetrics:
          props.config.aggregateMetrics ??
          runsLinePlotConfigDefaults.aggregateMetrics,
        isGrouped: isRunsetGrouped(runsetId),
        isFullFidelity,
        isSmoothed: smoothingWeight != null && smoothingWeight !== 0,
        legendFields:
          props.config.legendFields ?? runsLinePlotConfigDefaults.legendFields,
        metrics: props.config.metrics ?? runsLinePlotConfigDefaults.metrics,
        isSingleRun,
        type: props.config.plotType ?? runsLinePlotConfigDefaults.plotType,
        useRunsets: props.runSets != null && props.runSets.length > 1,
      }),
    [
      props.config,
      props.runSets,
      isRunsetGrouped,
      isFullFidelity,
      smoothingWeight,
      isSingleRun,
    ]
  );

  const isNgwLinePlotPickerEnabled = useIsNgwEnabled();
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const shouldUseNgwRegexMetrics =
    isNgwLinePlotPickerEnabled && workspaceRefs != null;

  const regexMetricsConfig = useMemo(
    () => ({
      metricRegex: props.config.metricRegex,
      metrics: props.config.metrics,
      useMetricRegex: props.config.useMetricRegex,
    }),
    [
      props.config.metricRegex,
      props.config.metrics,
      props.config.useMetricRegex,
    ]
  );

  // In regex mode, config.metrics is derived from the saved regex and must be
  // re-resolved when the panel loads. NGW resolves it server-side (RE2 via
  // metricSearch); otherwise the legacy history-key resolver runs.
  useRegexMetrics(regexMetricsConfig, props.updateConfig, {
    enabled: !shouldUseNgwRegexMetrics,
  });
  useRegexMetricsSync(
    regexMetricsConfig,
    props.updateConfig,
    workspaceRefs?.runSetRefs,
    workspaceRefs?.customRunNamesRef,
    {enabled: shouldUseNgwRegexMetrics}
  );

  const errorContext = useMemo(
    () => ({
      config: props.config,
      // @ts-ignore panel settings exists but the types are wrong higher up
      panelSettings: props?.panelSettings ?? {},
      query: {
        entityName: props.query.entityName,
        projectName: props.query.projectName,
      },
    }),
    // @ts-ignore
    // oxlint-disable-next-line react/preserve-manual-memoization
    [props.config, props?.panelSettings, props.query]
  );

  const handleBucketingError = React.useCallback(
    (error: Error) => {
      setRenderError(true);
      const details = {
        ...errorContext,
        message: error.message,
        panelType: 'bucketed-plot',
      };
      logError(
        'Full fidelity bucketing error, falling back to sampling',
        // @ts-ignore can just log key value pairs
        details
      );
    },
    [errorContext, setRenderError]
  );

  const configZoom = useDeepEqualValue({
    xAxisMin: props.config.xAxisMin ?? undefined,
    xAxisMax: props.config.xAxisMax ?? undefined,
    yAxisMin: props.config.yAxisMin ?? undefined,
    yAxisMax: props.config.yAxisMax ?? undefined,
  });

  /**
   * Notes on Panel Zoom Contexts:
   * There are currently two zoom contexts while we transition towards full fidelity mode.
   *
   * Previously the backend could only handle API queries in terms of _step units on the x-axis, this meant that we needed a complicated (and brittle) system to translate zoom interactions back in to _step units for custom x-axis values. This required an elaborate data wrapping layer which we could use to set bounds on the zoomed range. It was also a pain to keep from inadvertently starting an infinite fetch loop (where a zoom would trigger a data request, which would return data causing the boundary values to update, causing a new fetch, etc...)
   *
   * Updates the the backend now allow us to query the API directly in terms of whatever x-axis units we want. This means we can kill all the extra wrapping layers, the elaborate utility functions that support the boundary calcuations, and all the associated unit tests. BUT we can't move RSDQs to the new zooming layer yet because of complexities within the API itself resolving the return data.
   *
   * So for now we wrap everything in both the zoom contexts, and each interior component ends up using the functions that it needs. It gets a little messing the Graph.tsx and Config.tsx components which have to correctly target the proper context based on if they're full fidelity mode or not. This should be temporary complexity as we hope to deprecate sampling mode in the near future.
   */
  return (
    <PanelDataContextProvider>
      <PanelInteractionProvider
        activeRunLimit={props.config.limit}
        fullFidelityMode={fullFidelityMode}
        groupedFullFidelityMode={props.config.groupedFullFidelityMode}>
        <PanelTimeProvider>
          <PanelZoomContextProvider
            xAxisKey={xAxis}
            updateConfig={props.updateConfig}
            zoomConfig={configZoom}>
            <PanelZoomContext2Provider
              coordinateZooming={coordinateZooming}
              configZoom={configZoom}
              sharedPanelZoomConfig={sharedPanelZoom}
              xAxisKey={xAxis}>
              <PanelConfigContextProvider
                config={props.config}
                data={props.data}
                getDefaultLegendTemplateByRunsetId={
                  getDefaultLegendTemplateByRunsetId
                }
                isFullFidelity={isFullFidelity}>
                <PanelSystemsMetricsProvider
                  metrics={props.config.metrics ?? noOpArr}>
                  {isFullFidelity ? (
                    <ErrorBoundary
                      // Falling back to sampling is the recovery, so a
                      // bucketing failure must not open the full-page crash
                      // report dialog over an otherwise working workspace.
                      silent
                      onError={handleBucketingError}
                      renderError={renderNothing}>
                      <PanelOutliersRunsLinePlot {...props} />
                    </ErrorBoundary>
                  ) : (
                    <PanelRunsLinePlot {...props} />
                  )}
                </PanelSystemsMetricsProvider>
              </PanelConfigContextProvider>
            </PanelZoomContext2Provider>
          </PanelZoomContextProvider>
        </PanelTimeProvider>
      </PanelInteractionProvider>
    </PanelDataContextProvider>
  );
};

const WrapperWithContext = (props: RunsLinePlotPanelProps) => {
  // Notes on runsets: https://www.notion.so/wandbai/Run-set-67004423b75d4e5fbab4846ffdbc4ee9?pvs=4
  // Note: even on a single run workspace a runset is constructed
  const runSets = useMemo(() => getRunSets(props.pageQuery), [props.pageQuery]);
  const {useRunsTableGroupingInPanels} = useLinePlotSettingsWithDefaults(
    props.config
  );

  return (
    <PanelGroupingContextProvider
      aggregate={props.config.aggregate ?? false}
      aggregateMetrics={props.config.aggregateMetrics ?? false}
      groupAgg={props.config.groupAgg}
      groupArea={props.config.groupArea}
      groupBy={props.config.groupBy ?? ''}
      runSets={runSets}
      useRunsTableGroupingInPanels={useRunsTableGroupingInPanels}>
      <RunsLinePlotContextProvider
        config={props.config}
        updateConfig={props.updateConfig}>
        <Wrapper {...props} runSets={runSets} />
      </RunsLinePlotContextProvider>
    </PanelGroupingContextProvider>
  );
};

export default WrapperWithContext;
